import { useId, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { useDuplicateQuote } from '@/shared/quotes/useQuotes';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import {
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  MailIcon,
  RefreshIcon,
  UsersIcon,
} from '@/shared/ui/icons';

import { money } from './format';
import { SendQuoteDialog, Sendings } from './SendQuote';
import { QUOTE_STATUS_LABELS, type Quote } from './types';
import {
  useCancelQuote,
  useDownloadDraftPdf,
  useMarkSent,
  useQuotePdf,
  useRetryPdf,
  useReviewQuote,
} from './useQuotes';

/**
 * The end of a quote's life, in the order it happens: aprobar, descargar, enviar, anular.
 *
 * Every transition belongs to the API and none of the rules are reimplemented here (ADR
 * 0006 of the back): the front calls the endpoint and shows what comes back. What this
 * component decides is only what to *offer*, and it offers exactly one next step at a
 * time, because the states are not a menu. That one step is the only primary button of
 * the screen: «Elegir cliente» while there is none (it takes the focus to the picker),
 * «Aprobar» once there is one, «Descargar PDF» once approved. Approving without a
 * customer is possible and quiet: a ghost button with its warning.
 *
 * **Approving is the point of no return.** It burns a number out of the fiscal series,
 * freezes the customer's card into the quote and renders the document. A quote that turns
 * out wrong afterwards is cancelled, not corrected, and the number is kept so the series
 * has no holes. That is why the button says what it is about to do. And what was
 * cancelled (or closed) by mistake is **redone**, never un-cancelled: «Rehacer» asks the
 * API for a fresh draft with the same lines (contract v10.7) and goes there.
 *
 * The primary action lives once, in a fixed summary bar on every screen size. The
 * sidebar holds the explanation and secondary actions, away from the approval button.
 */
export function QuoteActions({
  quote,
  blocked,
  dirty = false,
  onSaveLines,
  onChooseClient,
  className,
}: {
  quote: Quote;
  blocked: string | null;
  dirty?: boolean;
  /** Save the lines on screen; resolves to whether they were saved. */
  onSaveLines?: () => Promise<boolean>;
  /** Take the person to the customer picker. */
  onChooseClient: () => void;
  className?: string;
}) {
  const review = useReviewQuote(quote.id);
  const markSent = useMarkSent(quote.id);
  const cancel = useCancelQuote(quote.id);
  const redo = useDuplicateQuote(quote.id);
  const draftPdf = useDownloadDraftPdf(quote.id);
  // «Guardar y descargar borrador»: the save before the download, and whether it failed
  // (the reason is on the lines card, which may be out of view).
  const [savingForDraft, setSavingForDraft] = useState(false);
  const [draftSaveFailed, setDraftSaveFailed] = useState(false);
  const navigate = useNavigate();
  const blockedId = useId();
  // Cancelling is as final as approving: it cannot be undone, and on a quote already sent
  // it turns the document in somebody's inbox into a cancelled one. So it asks first, in
  // a sheet that says what it is about to do.
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // «Enviar por correo» (contract v10.26) opens its sheet; the defaults come with the quote.
  const [sending, setSending] = useState(false);
  const sendDefaults = quote.send_defaults ?? null;

  const isDraft = quote.status === 'draft';
  const isCancelled = quote.status === 'cancelled';
  // The PDF only exists once a number does, and the worker takes a moment after the
  // approval: the hook keeps asking while the API answers "still rendering". Shared with
  // the preview in the page through the query cache: one request, two readers.
  const pdf = useQuotePdf(quote.id, !isDraft && !isCancelled);

  /**
   * The draft's PDF with what is on screen. Unsaved lines are saved first, with the same
   * save as «Guardar líneas» (validation included), and the PDF is asked for only once
   * the API has them: a PDF of the old lines next to a table showing the new ones would
   * be the one thing this button must never do.
   */
  async function downloadDraft(): Promise<void> {
    setDraftSaveFailed(false);
    if (dirty) {
      if (!onSaveLines) return;
      setSavingForDraft(true);
      const saved = await onSaveLines();
      setSavingForDraft(false);
      if (!saved) {
        setDraftSaveFailed(true);
        return;
      }
    }
    draftPdf.mutate();
  }

  // Anything else still settling (the customer being saved, the amounts being
  // recalculated, the offer being read) would print a document that is about to change.
  const draftWaiting = blocked !== null && !dirty;

  function redoQuote() {
    redo.mutate(undefined, { onSuccess: (draft) => void navigate(`/presupuestos/${draft.id}`) });
  }

  const approve = (
    <Button
      variant={quote.client_id ? 'primary' : 'ghost'}
      size={quote.client_id ? 'lg' : 'md'}
      icon={quote.client_id ? <CheckIcon size={18} /> : undefined}
      onClick={() => review.mutate()}
      disabled={review.isPending || blocked !== null}
      aria-describedby={blocked ? blockedId : undefined}
    >
      {review.isPending
        ? 'Aprobando…'
        : quote.client_id
          ? 'Aprobar y generar PDF'
          : 'Aprobar sin cliente'}
    </Button>
  );

  /** The one primary control of the current step, or null when there is none. */
  const primary: ReactNode = isDraft ? (
    quote.client_id ? (
      approve
    ) : (
      <Button size="lg" icon={<UsersIcon size={18} />} onClick={onChooseClient}>
        Elegir cliente
      </Button>
    )
  ) : isCancelled ? (
    // The only thing left to do with a cancelled quote, so it is the primary step and
    // shows in the fixed bar on a phone too.
    <Button size="lg" icon={<CopyIcon size={18} />} onClick={redoQuote} disabled={redo.isPending}>
      {redo.isPending ? 'Rehaciendo…' : 'Rehacer presupuesto'}
    </Button>
  ) : quote.status === 'reviewed' && sendDefaults && !sendDefaults.blocked_reason ? (
    // Contract v10.26: approved, the next step is sending it, from here.
    <Button size="lg" icon={<MailIcon size={18} />} onClick={() => setSending(true)}>
      Enviar por correo
    </Button>
  ) : pdf.data ? (
    // No `download` attribute: browsers ignore it on a link to another origin, and
    // the bucket is another origin. The file name and the `attachment` come inside
    // the signed URL instead (see the back's `storage.signed_url`), which is also
    // what stops this from navigating the app away into the native viewer.
    <a
      href={pdf.data.url}
      target="_blank"
      rel="noopener noreferrer"
      className="bg-accent hover:bg-accent-2 inline-flex h-11 items-center justify-center gap-2 rounded-md px-5 text-[15px] font-medium text-white shadow-sm"
    >
      <DownloadIcon size={18} />
      Descargar PDF
    </a>
  ) : null;

  const title = isDraft
    ? 'Documento'
    : isCancelled
      ? 'Anulado'
      : `${QUOTE_STATUS_LABELS[quote.status]}`;

  return (
    <>
      <Card title={title} {...(className ? { className } : {})}>
        {isDraft ? (
          <>
            {!quote.client_id ? (
              <p className="text-ink-3 text-[12.5px] leading-snug">
                Sin cliente, el documento saldrá con el bloque del cliente en blanco.
              </p>
            ) : null}
            {!quote.client_id ? <div>{approve}</div> : null}
            <p className="text-ink-3 text-[12.5px] leading-snug">
              Aprobar da el número definitivo y genera el PDF. Después no se edita: se anula y se
              rehace.
            </p>
            {/* The look before the leap (contract v10.18): the same document, with BORRADOR
                where the number goes. It spends nothing, so it is offered freely. It prints
                what is saved, so with unsaved lines it saves them first. */}
            <div className="border-line-2 flex flex-col items-start gap-1.5 border-t pt-3">
              <Button
                variant="secondary"
                size="sm"
                icon={<DownloadIcon size={15} />}
                onClick={() => void downloadDraft()}
                disabled={savingForDraft || draftPdf.isPending || draftWaiting}
                aria-describedby={draftWaiting ? blockedId : undefined}
              >
                {savingForDraft
                  ? 'Guardando líneas…'
                  : draftPdf.isPending
                    ? 'Preparando el PDF…'
                    : dirty
                      ? 'Guardar y descargar borrador'
                      : 'Descargar borrador en PDF'}
              </Button>
              <p className="text-ink-3 text-[12.5px] leading-snug">
                {dirty
                  ? 'Guarda las líneas y lo descarga con los cambios. No aprueba nada.'
                  : 'Para ver cómo va a salir. Sale sin número y no aprueba nada.'}
              </p>
              {draftSaveFailed ? (
                <p role="alert" className="text-danger text-[12.5px] leading-snug">
                  No se pudieron guardar las líneas, así que no se ha descargado. El motivo está en
                  la tabla de líneas.
                </p>
              ) : null}
              {draftPdf.isError ? (
                <ErrorMessage error={draftPdf.error}>No se pudo preparar el borrador</ErrorMessage>
              ) : null}
            </div>
          </>
        ) : null}

        {!isDraft && !isCancelled ? <Sendings quote={quote} /> : null}

        {!isDraft && !isCancelled && sendDefaults ? (
          <div className="flex flex-col items-start gap-1.5">
            {quote.status === 'sent' || sendDefaults.blocked_reason ? (
              <Button
                variant="secondary"
                size="sm"
                icon={<MailIcon size={15} />}
                onClick={() => setSending(true)}
              >
                {quote.status === 'sent' ? 'Enviar otra vez' : 'Enviar por correo'}
              </Button>
            ) : null}
            {sendDefaults.blocked_reason ? (
              <p className="text-warn text-[12.5px] leading-snug">{sendDefaults.blocked_reason}</p>
            ) : null}
          </div>
        ) : null}

        {quote.status === 'reviewed' ? (
          <div className="border-line-2 flex flex-col items-start gap-1.5 border-t pt-3">
            {/* The primary step is sending it; the PDF stays one click away, for whoever
                prefers to send it from their own mail. */}
            {pdf.data && sendDefaults && !sendDefaults.blocked_reason ? (
              <a
                href={pdf.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="link inline-flex items-center gap-1.5 text-[13.5px]"
              >
                <DownloadIcon size={15} />
                Descargar PDF
              </a>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => markSent.mutate()}
              disabled={markSent.isPending}
            >
              {markSent.isPending ? 'Anotando…' : 'Marcar como enviado'}
            </Button>
            <p className="text-ink-3 text-[12.5px] leading-snug">
              Si lo has mandado desde tu correo, anótalo aquí. «Enviar por correo» lo anota solo.
            </p>
          </div>
        ) : null}

        {markSent.isError ? (
          <ErrorMessage error={markSent.error}>No se pudo marcar como enviado</ErrorMessage>
        ) : null}

        {!isCancelled ? (
          <div className="border-line-2 flex flex-wrap items-center gap-2 border-t pt-3">
            {!isDraft ? (
              <Button
                variant="secondary"
                size="sm"
                icon={<CopyIcon size={15} />}
                onClick={redoQuote}
                disabled={redo.isPending}
              >
                {redo.isPending ? 'Rehaciendo…' : 'Rehacer'}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setConfirmingCancel(true)}>
              Anular
            </Button>
            {!isDraft ? (
              <p className="text-ink-3 w-full text-[12.5px] leading-snug">
                «Rehacer» crea un borrador nuevo con estas líneas; este se queda como está.
              </p>
            ) : null}
            {redo.isError ? (
              <ErrorMessage error={redo.error}>No se pudo rehacer</ErrorMessage>
            ) : null}
            {sending && sendDefaults ? (
              <SendQuoteDialog
                quote={quote}
                defaults={sendDefaults}
                onClose={() => setSending(false)}
              />
            ) : null}
            {confirmingCancel ? (
              <CancelDialog
                quote={quote}
                busy={cancel.isPending}
                error={cancel.isError ? cancel.error : undefined}
                onCancel={() => {
                  setConfirmingCancel(false);
                  cancel.reset();
                }}
                onConfirm={() =>
                  cancel.mutate(undefined, { onSuccess: () => setConfirmingCancel(false) })
                }
              />
            ) : null}
          </div>
        ) : (
          <p className="text-ink-3 text-[13px]">
            Se descartó. Si tenía número, se conserva anulado: una serie con huecos no es una serie.
            «Rehacer» copia sus líneas y avisos a un borrador nuevo, con la misma oferta detrás.
          </p>
        )}
      </Card>

      {primary ? (
        <div
          role="region"
          aria-label="Siguiente paso"
          className="bg-card border-line fixed inset-x-0 bottom-[calc(3.5rem+1px+env(safe-area-inset-bottom))] z-20 border-t shadow-[0_-4px_20px_0_rgb(0_0_0/0.04)] lg:bottom-0 lg:left-56 lg:pb-[env(safe-area-inset-bottom)]"
        >
          <div className="mx-auto flex max-w-[1400px] flex-col gap-2 px-4 py-3 sm:px-6 lg:px-10">
            {review.isError ? (
              <ErrorMessage error={review.error}>No se pudo aprobar</ErrorMessage>
            ) : null}
            {isCancelled && redo.isError ? (
              <ErrorMessage error={redo.error}>No se pudo rehacer</ErrorMessage>
            ) : null}
            {isDraft && blocked ? (
              <p id={blockedId} aria-live="polite" className="text-warn text-[13px] leading-snug">
                {blocked}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
              <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-1">
                <p className="text-ink flex items-baseline gap-2 text-[14px]">
                  <span className="text-ink-3">{dirty ? 'Total guardado' : 'Total'}</span>
                  <span className="font-display text-[22px] font-semibold tabular-nums">
                    {money(quote.total_gross)}
                  </span>
                </p>
                <p className="text-ink-3 hidden text-[13px] xl:block">
                  {isDraft
                    ? 'Al aprobar se genera el PDF y se cierra la edición.'
                    : quote.quote_number}
                </p>
              </div>
              <div className="flex w-full items-center sm:w-auto [&>*]:w-full sm:[&>*]:w-auto">
                {primary}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * The question before cancelling, as a sheet: which quote, what cancelling does to it,
 * and two answers. The text changes with the state, because cancelling a sent quote is
 * not the same as discarding a draft: the first turns a document in somebody's inbox
 * into a cancelled one. The API's refusal, if any, lands inside the sheet.
 */
function CancelDialog({
  quote,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  quote: Quote;
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const name = quote.quote_number ?? 'Este borrador';
  return (
    <Dialog
      title="¿Anular este presupuesto?"
      size="sm"
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            No, volver
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={busy} data-autofocus>
            {busy ? 'Anulando…' : 'Sí, anular'}
          </Button>
        </>
      }
    >
      <p className="text-ink-2 text-[14.5px] leading-relaxed">
        {quote.status === 'sent' ? (
          <>
            <strong className="text-ink">{name}</strong> ya se envió al cliente: anularlo convierte
            en anulado el documento que tiene en su bandeja. Anular no tiene vuelta atrás y el
            número se conserva.
          </>
        ) : quote.status === 'draft' ? (
          <>
            <strong className="text-ink">{name}</strong> se descarta. Anular no tiene vuelta atrás:
            si más adelante hace falta, podrás rehacerlo desde el anulado.
          </>
        ) : (
          <>
            <strong className="text-ink">{name}</strong> pasa a anulado. Anular no tiene vuelta
            atrás y el número se conserva: una serie con huecos no es una serie. Podrás rehacerlo
            desde el anulado.
          </>
        )}
      </p>
      {error ? <ErrorMessage error={error}>No se pudo anular</ErrorMessage> : null}
    </Dialog>
  );
}

/**
 * The document, once it exists: a preview and, until then, the wait or the way out.
 *
 * Rendered by the page in the wide column (a PDF in a 300 px column is a stamp), reading
 * the same query as the card above.
 */
export function PdfPreview({ quote, className }: { quote: Quote; className?: string }) {
  const enabled = quote.status !== 'draft' && quote.status !== 'cancelled';
  const pdf = useQuotePdf(quote.id, enabled);
  const retry = useRetryPdf(quote.id);
  if (!enabled) return null;

  if (pdf.isPending) {
    return (
      <div className={className}>
        <Spinner label="Generando el PDF" />
      </div>
    );
  }
  if (pdf.isError) {
    return (
      <Callout
        tone="warn"
        title="El PDF todavía no está"
        {...(className ? { className } : {})}
        action={
          // A button and not "reload the page": if the enqueue was lost (Redis down for
          // the instant of the approval) reloading asks for a file nobody is making. This
          // asks the worker again, which is idempotent because the key is the number.
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshIcon size={15} />}
            disabled={retry.isPending}
            onClick={() => retry.mutate(undefined, { onSuccess: () => void pdf.refetch() })}
          >
            {retry.isPending ? 'Pidiéndolo…' : 'Volver a generarlo'}
          </Button>
        }
      >
        <ErrorMessage error={pdf.error} tone="warning" className="border-0 bg-transparent p-0" />
      </Callout>
    );
  }
  if (!pdf.data) return null;

  return (
    <figure className={className}>
      {/* `no-referrer` because the address of this page carries the quote's id, and the
          bucket has no business being told which quote is being looked at. No `sandbox`:
          Chrome's built-in PDF viewer needs scripts and same-origin, and a blanket
          sandbox turns the preview into a blank rectangle. The document is rendered by
          this system, not by a third party.

          `preview_url`, not `url`: the download link carries `attachment` inside its
          signature, and Chrome does not paint an attachment inside a frame, it downloads
          it and leaves the frame blank (seen 2026-09-11 in a real browser). The preview
          is the same object signed `inline`. */}
      <iframe
        title={`Vista previa de ${quote.quote_number ?? 'el presupuesto'}`}
        src={pdf.data.preview_url}
        referrerPolicy="no-referrer"
        className="border-line h-[36rem] w-full rounded-lg border bg-white"
      />
      {/* The link is temporary by design: it is a signed URL, and the bytes never travel
          through the API. Saying when it expires stops "this link stopped working" from
          looking like a bug. */}
      <figcaption className="text-ink-3 mt-1 text-[12.5px]">
        Enlace temporal, caduca en {Math.round(pdf.data.expires_in / 60)} minutos. Recarga la página
        para pedir otro.
      </figcaption>
    </figure>
  );
}
